/**
 * B3: Persona-aware navigation configuration.
 *
 * Defines which sidebar items are visible for each persona.
 * The sidebar component reads this to filter its NAV_ITEMS.
 *
 * Persona → visible nav paths:
 *   operator: CRM, Email, Pages, Workflows, Analytics, Marketplace, Settings, Inbox
 *   buyer:    Messages, Bookings, Invoices, Settings
 *   agency:   Everything operator sees + Agency admin + mounted affiliate portal
 *   affiliate: Dashboard, mounted affiliate portal, Marketplace, Settings
 */
import type { Persona } from "@/providers/persona-context";

/** Nav paths visible per persona. "/dashboard" is always visible. */
const PERSONA_NAV: Record<Persona, string[]> = {
  operator: [
    "/dashboard",
    "/inbox",
    "/crm",
    "/services",
    "/email",
    "/pages",
    "/webinars",
    "/workflows",
    "/ai",
    "/communications",
    "/analytics",
    "/marketplace",
    "/reputation",
    "/briefing",
    "/community",
    "/courses",
    "/settings",
  ],
  buyer: [
    "/dashboard",
    "/messages",
    "/bookings",
    "/invoices",
    "/settings",
  ],
  agency: [
    "/dashboard",
    "/inbox",
    "/crm",
    "/services",
    "/email",
    "/pages",
    "/webinars",
    "/workflows",
    "/ai",
    "/communications",
    "/analytics",
    "/marketplace",
    "/reputation",
    "/briefing",
    "/community",
    "/courses",
    "/affiliate",
    "/settings",
    "/agency",
  ],
  affiliate: [
    "/dashboard",
    "/affiliate",
    "/marketplace",
    "/settings",
  ],
};

/**
 * Returns true if a nav path should be visible for the given persona.
 * Returns true for ALL paths if persona is null (first-time user sees everything).
 */
export function isNavVisibleForPersona(path: string, persona: Persona | null): boolean {
  if (persona === null) return true; // No persona set → show everything
  const allowedPaths = PERSONA_NAV[persona];
  return allowedPaths.some((allowed) => path === allowed || path.startsWith(allowed + "/"));
}
